import React, { useState } from 'react';
import { Modal } from 'react-native';
import styled from 'styled-components/native';
import { Feather, Ionicons } from '@expo/vector-icons';
import { B, P } from '../../components/Typography';
import ListItem from '../../components/list/ListItem';
import Colors from '../../constants/Colors';
import { useLocationContext } from '../../context/LocationContext';
import LocationPrimingScreen from './LocationPrimingScreen';

// Mirrors MyDataList's StyledListItem so the card sits in the measurement
// list's visual rhythm: same height, spacing and frame treatment, with the
// border tinted amber in the needs-attention state the way stale
// measurements tint theirs pink.
const StyledListItem = styled(ListItem)<{ attention?: boolean }>`
  height: 84px;
  margin-bottom: 12px;
  padding: 0 6px 0 12px;
  ${p =>
    p.attention
      ? `border-color: #FFD59E; border-bottom-color: #FFD59E;`
      : ''}
`;

// Measurement rows lead with a 40x40 icon tile; location has no wearable
// image asset, so build the tile from the same geometry.
const StyledIconTile = styled.View<{ attention?: boolean }>`
  height: 40px;
  width: 40px;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  background-color: ${p => (p.attention ? '#FFF4E5' : '#EDEBF7')};
`;

const StyledDetailContainer = styled.View`
  flex: 1;
  margin: 0 0 0 8px;
`;

const StyledRowContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledStatusText = styled(B)<{ attention?: boolean }>`
  /* #7A4500 (not the icon's #B26A00): 7.2:1 on white — text needs 4.5:1
     where the icon only needs 3:1. Active matches the rows' value color. */
  color: ${p => (p.attention ? '#7A4500' : Colors.darkText)};
  margin: 0 6px 0 0;
  text-align: right;
`;

// Same grey the measurement rows use for their date/source line.
const StyledSubtitleText = styled(P)`
  color: #858ca2;
`;

/**
 * Participant-facing location sharing status (CD-295).
 *
 * Two audiences. Participants get a persistent, discoverable answer to "is
 * it working?" and a route back to a working grant — the priming flow
 * otherwise appears at most once per session, so a participant who dismissed
 * it had no way back without backgrounding tricks. Store reviewers get the
 * user-visible feature both stores require background location to power:
 * Apple must be able to verify the background mode does something for the
 * user (2.5.4), and Google's declaration video has to show a user-facing
 * feature, not just a permission dialog.
 *
 * Deliberately shows NO location data — no coordinates, no map, no homestay
 * numbers. The privacy stance is that participants' location detail is never
 * displayed anywhere, and this card only reports whether sharing is working.
 * The full explanation of what is collected lives on the tap-through
 * priming screen, so the row copy stays list-length.
 *
 * Renders nothing unless the server gate says capture applies (GPS-enabled
 * study + eligible enrollment), so participants in ordinary studies never
 * see a location surface at all. On web `enabled` never resolves true, which
 * hides it there for free.
 */
const LocationSharingCard: React.FC = () => {
  const { enabled, permissionLevel } = useLocationContext();
  const [showFlow, setShowFlow] = useState(false);

  if (enabled !== true) return null;

  const active = permissionLevel === 'always';
  const status = active ? 'Active' : 'Needs attention';
  const subtitle = active
    ? 'Only a daily summary is shared'
    : 'Tap to finish setting up';

  return (
    <>
      <StyledListItem
        framed
        filled
        attention={!active}
        accessibilityRole='button'
        // Label must carry the STATE: a static label overrides the
        // descendant text, so VoiceOver/TalkBack would announce both states
        // identically — and the degraded state is exactly the one a screen
        // reader user must not miss.
        accessibilityLabel={`Location sharing ${status.toLowerCase()}. ${subtitle}.`}
        accessibilityHint='Opens the location sharing setup screen'
        onPress={() => setShowFlow(true)}
      >
        <StyledIconTile attention={!active}>
          <Ionicons
            name={active ? 'location' : 'warning-outline'}
            size={22}
            color={active ? '#4E439A' : '#B26A00'}
          />
        </StyledIconTile>
        <StyledDetailContainer>
          <StyledRowContainer>
            <B color='dark'>Location sharing</B>
            <StyledRowContainer>
              <StyledStatusText attention={!active}>{status}</StyledStatusText>
              {/* Feather chevron-right, 22, black — what MyDataList rows use */}
              <Feather name='chevron-right' size={22} color='black' />
            </StyledRowContainer>
          </StyledRowContainer>
          <StyledSubtitleText numberOfLines={1}>{subtitle}</StyledSubtitleText>
        </StyledDetailContainer>
      </StyledListItem>
      {showFlow && (
        <Modal
          animationType='slide'
          visible
          onRequestClose={() => setShowFlow(false)}
        >
          <LocationPrimingScreen onDone={() => setShowFlow(false)} />
        </Modal>
      )}
    </>
  );
};

export default LocationSharingCard;
